import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ExternalLink, Maximize2, X } from "lucide-react";
import { driveEmbedUrl, driveViewUrl } from "@/config/pricing";
import { cn } from "@/lib/utils";

const unlock = () => {
  try {
    window.screen?.orientation?.unlock?.();
  } catch {}
};

// Jendela contoh video. Video Drive baru dimuat saat jendela dibuka.
// Di HP: jendela memenuhi layar, video selebar layar, dan ada tombol layar penuh.
export const SampleModal = ({ title, styles, initialId, onPick, onClose }) => {
  const [activeId, setActiveId] = useState(initialId || styles[0].id);
  const active = styles.find((s) => s.id === activeId) || styles[0];
  const box = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    const onFs = () => !document.fullscreenElement && unlock();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      document.body.style.overflow = prev;
      unlock();
    };
  }, [onClose]);

  // Layar penuh + putar ke mendatar. Kalau browser (mis. iPhone) tidak mendukung, buka di Drive.
  const fullscreen = async () => {
    const el = box.current;
    const request = el?.requestFullscreen || el?.webkitRequestFullscreen;
    if (!request) return window.open(driveViewUrl(active.drive), "_blank", "noopener,noreferrer");
    try {
      await request.call(el);
      try {
        await window.screen?.orientation?.lock?.("landscape");
      } catch {}
    } catch {
      window.open(driveViewUrl(active.drive), "_blank", "noopener,noreferrer");
    }
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      data-testid="sample-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Contoh video ${title}`}
        onClick={(e) => e.stopPropagation()}
        className="flex h-[100dvh] w-full max-w-3xl flex-col overflow-y-auto bg-white px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1.25rem,env(safe-area-inset-top))] sm:h-auto sm:max-h-[92vh] sm:p-7"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-[#B9B2A4]">Watch samples</p>
            <h3 className="mt-2 font-serif text-3xl font-light text-[#1C1C1C]">{title}</h3>
          </div>
          <button type="button" aria-label="Tutup" onClick={onClose} className="-mr-2 flex h-11 w-11 items-center justify-center text-[#8A8A8A] hover:text-[#1C1C1C]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
          {styles.map((s) => (
            <button
              key={s.id}
              type="button"
              aria-pressed={s.id === active.id}
              onClick={() => setActiveId(s.id)}
              className={cn(
                "shrink-0 border px-4 py-2 font-sans text-xs transition-colors",
                s.id === active.id
                  ? "border-[#1C1C1C] bg-[#1C1C1C] text-white"
                  : "border-[#E8E3D9] bg-white text-[#5C5C5C] hover:border-[#B9B2A4]"
              )}
            >
              {s.name}
            </button>
          ))}
        </div>

        {/* Di HP video melebar sampai tepi layar (-mx-5) supaya tidak kecil */}
        <div ref={box} className="relative -mx-5 mt-4 aspect-video bg-[#1C1C1C] sm:mx-0">
          <iframe
            key={active.id}
            src={driveEmbedUrl(active.drive)}
            title={`Contoh video ${active.name}`}
            className="absolute inset-0 h-full w-full"
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <p className="font-sans text-sm text-[#5C5C5C]">
            <span className="text-[#1C1C1C]">{active.name}</span> · {active.mood}
          </p>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={fullscreen}
              className="inline-flex min-h-[44px] items-center gap-1.5 font-sans text-xs uppercase tracking-widest text-[#1C1C1C] underline"
            >
              Layar penuh <Maximize2 className="h-3 w-3" />
            </button>
            <a
              href={driveViewUrl(active.drive)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] items-center gap-1.5 font-sans text-xs uppercase tracking-widest text-[#8A8A8A] underline hover:text-[#1C1C1C]"
            >
              Buka di Drive <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {onPick && (
          <button
            type="button"
            onClick={() => onPick(active.id)}
            className="mt-6 w-full bg-[#1C1C1C] px-6 py-4 font-sans text-sm uppercase tracking-widest text-white transition-colors hover:bg-[#333333]"
          >
            Pilih gaya {active.name}
          </button>
        )}
      </div>
    </motion.div>,
    document.body
  );
};
